/**
 * Gemini Live API transport (google_ai_studio / gemini-3.8-live).
 *
 * A stateful WebSocket straight from the browser to
 * `wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1beta.GenerativeService.BidiGenerateContentConstrained`
 * authenticated with an EPHEMERAL token the worker minted (`?access_token=`) — the real
 * Google key never leaves the worker. Audio: mic PCM16 @ 16 kHz in (`realtimeInput.audio`,
 * mimeType `audio/pcm;rate=16000`), model PCM16 @ 24 kHz out (`serverContent.modelTurn.parts[].inlineData`).
 * Turn-taking is the server's automatic activity detection; `serverContent.interrupted`
 * = the child barged in → drop the queued audio.
 *
 * The hooks speak OpenAI-Realtime event names, so this transport TRANSLATES both ways and
 * exposes the same {callId, send, close} channel:
 *   server → hook:  input transcription → conversation.item.input_audio_transcription.updated/completed,
 *                   output transcription → response.output_audio_transcript.delta/done,
 *                   audio chunk → response.output_audio.delta (no payload), interrupted →
 *                   input_audio_buffer.speech_started, turnComplete → response.done,
 *                   toolCall → response.function_call_arguments.done {call_id, name, arguments}
 *   hook → server:  conversation.item.create{function_call_output} → toolResponse,
 *                   response.create → ignored (Gemini answers a tool response on its own;
 *                   the greeting is kicked here as a user turn once setup completes).
 */
/** the channel both transports expose: the hook only ever uses these three */
export interface RealtimeChannel {
  callId: string | null;
  send: (event: unknown) => void;
  close: () => void;
  /** the agent's current loudness 0..1 (optional; the xAI transport reports it) */
  level?: () => number;
}

/** the *Constrained* method is the one that accepts an ephemeral token (`?access_token=`); the
 *  plain BidiGenerateContent wants a real API key and closes 1008 "unregistered callers" */
export const GEMINI_LIVE_WS_URL =
  "wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1beta.GenerativeService.BidiGenerateContentConstrained";

const IN_RATE = 16000;
const OUT_RATE = 24000;
const CHUNK_MS = 100;
const VAD_RMS = 0.015; // mic RMS above this = the child is talking (idle auto-close clock)

export interface GeminiSessionConfig {
  instructions: string;
  /** OpenAI-shaped tools ({type:'function', name, description, parameters}) — translated to functionDeclarations. */
  tools?: unknown[];
  /** a Gemini prebuilt voice name (Leda, Puck, Kore, …) — the worker maps the app's voice to one */
  voice: string;
  /** manifest id, e.g. gemini-3.8-live (the token is already locked to it) */
  model: string;
  /** Prompt (in the child's language) that kicks the agent's opening line; empty = let the child start. */
  greeting?: string;
}

export interface ConnectGeminiOptions {
  ephemeralKey: string;
  wsUrl?: string;
  /** which query param carries the credential: 'access_token' = Google direct (ephemeral token),
   *  'ticket' = the worker relay (one-use ticket; the worker meters + settles) */
  wsAuthParam?: 'access_token' | 'ticket';
  micStream: MediaStream;
  session: GeminiSessionConfig;
  onEvent: (event: Record<string, unknown>) => void;
  onError: (err: Error) => void;
  onOpen?: () => void;
  /** Fired once after `silenceMs` of child silence (agent not speaking). 0 disables. */
  onIdle?: () => void;
  silenceMs?: number;
}

/** Float32 [-1,1] at `from` Hz → base64 little-endian PCM16 at `to` Hz (linear resample). */
function floatToB64Pcm16(input: Float32Array, from: number, to: number): string {
  const n = from === to ? input.length : Math.round((input.length * to) / from);
  const pcm = new Int16Array(n);
  const step = from / to;
  for (let i = 0; i < n; i++) {
    const pos = i * step;
    const i0 = Math.floor(pos);
    const i1 = Math.min(input.length - 1, i0 + 1);
    const v = input[i0] + (input[i1] - input[i0]) * (pos - i0);
    const s = Math.max(-1, Math.min(1, v));
    pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  const bytes = new Uint8Array(pcm.buffer);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

/** base64 PCM16 → Float32Array [-1,1]. A chunk may end mid-sample (odd byte count): the stray byte
 *  is CARRIED into the next chunk — dropping it shifted every later sample by a byte and turned the
 *  rest of the phrase into a squeal. */
let pcmCarry: number | null = null;
function b64Pcm16ToFloat(b64: string): Float32Array {
  const bin = atob(b64);
  const off = pcmCarry == null ? 0 : 1;
  const total = bin.length + off;
  const even = total - (total % 2);
  const bytes = new Uint8Array(even);
  let j = 0;
  if (pcmCarry != null) bytes[j++] = pcmCarry;
  for (let i = 0; j < even; i++, j++) bytes[j] = bin.charCodeAt(i);
  pcmCarry = total % 2 ? bin.charCodeAt(bin.length - 1) : null;
  const pcm = new Int16Array(bytes.buffer, 0, even / 2);
  const out = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) out[i] = pcm[i] / (pcm[i] < 0 ? 0x8000 : 0x7fff);
  return out;
}
function resetPcmCarry(): void { pcmCarry = null; }
/** Google states the rate in the mimeType (`audio/pcm;rate=24000`); anything else is resampled to OUT_RATE. */
function withOutRate(f32: Float32Array, mime?: string): Float32Array {
  const m = /rate=(\d+)/.exec(mime ?? '');
  const r = m ? Number(m[1]) : OUT_RATE;
  if (!r || r === OUT_RATE) return f32;
  const n = Math.round((f32.length * OUT_RATE) / r);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) { const x = (i * r) / OUT_RATE; const i0 = Math.floor(x); const t = x - i0; out[i] = f32[i0] * (1 - t) + (f32[Math.min(i0 + 1, f32.length - 1)] ?? 0) * t; }
  return out;
}

/** OpenAI tool → Gemini functionDeclaration (Gemini's OpenAPI subset: no strict / additionalProperties / $schema). */
function toFunctionDeclarations(tools: unknown[] | undefined): { functionDeclarations: unknown[] }[] {
  const strip = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(strip);
    if (v && typeof v === "object") {
      const out: Record<string, unknown> = {};
      for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
        if (k === "additionalProperties" || k === "strict" || k === "$schema" || k === "default") continue;
        out[k] = strip(val);
      }
      return out;
    }
    return v;
  };
  const decls = (tools ?? [])
    .map((t) => t as { type?: string; name?: string; description?: string; parameters?: unknown; function?: { name?: string; description?: string; parameters?: unknown } })
    .map((t) => (t.function ? t.function : t))
    .filter((t) => typeof t.name === "string")
    .map((t) => ({ name: t.name, description: t.description ?? "", ...(t.parameters ? { parameters: strip(t.parameters) } : {}) }));
  return decls.length ? [{ functionDeclarations: decls }] : [];
}

export async function connectGeminiLive(opts: ConnectGeminiOptions): Promise<RealtimeChannel> {
  const { ephemeralKey, micStream, session, onEvent, onError, onOpen, onIdle } = opts;
  const silenceMs = opts.silenceMs ?? 0;
  const wsUrl = `${opts.wsUrl || GEMINI_LIVE_WS_URL}?${opts.wsAuthParam ?? 'access_token'}=${encodeURIComponent(ephemeralKey)}`;

  const ctx = new AudioContext();
  const rate = ctx.sampleRate;
  const source = ctx.createMediaStreamSource(micStream);
  const proc = ctx.createScriptProcessor(4096, 1, 1);

  // Playback = the reference AudioStreamer's shape (google-gemini/live-api-web-console): incoming
  // samples are ACCUMULATED into ~320 ms buffers and scheduled back-to-back on the context clock
  // with a look-ahead. One AudioBufferSourceNode per tiny network chunk is what WebKit crackles /
  // squeals on (bug 222098); big buffers also ride out jitter. The tail of a turn is flushed after
  // a short lull. A chunk that arrives after its slot restarts the timeline with the same lead.
  const BUF_SAMPLES = 7680; // 320 ms @ 24 kHz
  const LEAD_S = 0.1;
  const FLUSH_MS = 60;
  const sources = new Set<AudioBufferSourceNode>();
  let nextAt = 0;
  let playing = false;
  let pending: Float32Array[] = [];
  let pendingLen = 0;
  let flushTimer: ReturnType<typeof setTimeout> | null = null;
  const schedule = (f32: Float32Array) => {
    if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
    const buf = ctx.createBuffer(1, f32.length, OUT_RATE);
    buf.getChannelData(0).set(f32);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    const now = ctx.currentTime;
    if (nextAt < now + 0.005) nextAt = now + LEAD_S;
    src.start(nextAt);
    nextAt += buf.duration;
    playing = true;
    sources.add(src);
    src.onended = () => { sources.delete(src); if (sources.size === 0 && pendingLen === 0) playing = false; };
  };
  const drain = (all: boolean) => {
    while (pendingLen >= BUF_SAMPLES || (all && pendingLen > 0)) {
      const n = all ? pendingLen : BUF_SAMPLES;
      const out = new Float32Array(n);
      let w = 0;
      while (w < n) {
        const head = pending[0];
        const take = Math.min(head.length, n - w);
        out.set(head.subarray(0, take), w);
        w += take;
        if (take === head.length) pending.shift(); else pending[0] = head.subarray(take);
      }
      pendingLen -= n;
      schedule(out);
    }
  };
  const enqueue = (f32: Float32Array) => {
    if (!f32.length) return;
    pending.push(f32); pendingLen += f32.length; playing = true;
    drain(false);
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(() => { flushTimer = null; drain(true); }, FLUSH_MS);
  };
  const stopPlayback = () => {
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    pending = []; pendingLen = 0; resetPcmCarry();
    for (const s of sources) { try { s.stop(); s.disconnect(); } catch { /* stopped */ } }
    sources.clear();
    nextAt = 0;
    playing = false;
  };

  const ws = new WebSocket(wsUrl);
  ws.binaryType = "arraybuffer";
  const raw = (msg: unknown) => { if (ws.readyState === WebSocket.OPEN) { try { ws.send(JSON.stringify(msg)); } catch { /* closing */ } } };

  // The hook's OpenAI-shaped client events → Gemini messages.
  const pendingCalls = new Map<string, string>(); // call id → function name (toolResponse needs both)
  const send = (event: unknown) => {
    const e = (event ?? {}) as { type?: string; item?: { type?: string; call_id?: string; output?: string } };
    if (e.type === "conversation.item.create" && e.item?.type === "function_call_output" && e.item.call_id) {
      let result: unknown = e.item.output ?? "";
      try { result = JSON.parse(String(e.item.output ?? "")); } catch { /* plain text result */ }
      const name = pendingCalls.get(e.item.call_id) ?? "";
      pendingCalls.delete(e.item.call_id);
      raw({ toolResponse: { functionResponses: [{ id: e.item.call_id, name, response: typeof result === "object" && result !== null ? result : { result } }] } });
      return;
    }
    if (e.type === "conversation.item.create" && e.item?.type === "message") {
      const item = e.item as { content?: { type?: string; text?: string }[] };
      const text = (item.content ?? []).map((c) => c.text ?? "").join(" ").trim();
      if (text) raw({ clientContent: { turns: [{ role: "user", parts: [{ text }] }], turnComplete: true } });
      return;
    }
    /**
     * `response.create` with instructions = "answer now, like this".
     *
     * Gemini has no per-response instructions, and the Live API's own way to
     * make the model speak on demand is a client turn — so the order rides in
     * as one, marked as a note from the system so it is obeyed rather than
     * read out. A bare response.create (no instructions) stays ignored:
     * Gemini answers a tool response on its own.
     */
    if (e.type === "response.create") {
      const order = ((event ?? {}) as { response?: { instructions?: string } }).response?.instructions;
      if (order) raw({ clientContent: { turns: [{ role: "user", parts: [{ text: `[system] ${order}` }] }], turnComplete: true } });
      return;
    }
    // session.update: Gemini drives its own turns — nothing to send.
  };

  let configured = false;
  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    stopPlayback();
    try { proc.disconnect(); source.disconnect(); } catch { /* ignore */ }
    // The mic stream is OWNED BY THE HOOK (it survives idle-close); never stop its tracks here.
    void ctx.close().catch(() => {});
    try { ws.close(1000); } catch { /* closing */ }
  };

  let lastVoiceMs = Date.now();
  let responseActive = false;
  let idleFired = false;
  // transcripts accumulate per turn (Gemini streams fragments; the hooks want cumulative text)
  let userText = "";
  let agentText = "";
  let userItem = 0;
  let sawAudioThisTurn = false;

  ws.onopen = () => {
    try {
      if (ctx.state === "suspended") void ctx.resume().catch(() => {});
      raw({
        setup: {
          model: `models/${session.model}`,
          generationConfig: {
            responseModalities: ["AUDIO"],
            speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: session.voice } } },
          },
          systemInstruction: { parts: [{ text: session.instructions }] },
          tools: toFunctionDeclarations(session.tools),
          inputAudioTranscription: {},
          outputAudioTranscription: {},
          realtimeInputConfig: { automaticActivityDetection: {} },
        },
      });
      let buf: Float32Array[] = [];
      let total = 0;
      const chunkSamples = Math.round((rate * CHUNK_MS) / 1000);
      proc.onaudioprocess = (e) => {
        if (!configured || ws.readyState !== WebSocket.OPEN) return;
        const input = e.inputBuffer.getChannelData(0);
        let sum = 0;
        for (let i = 0; i < input.length; i++) sum += input[i] * input[i];
        const rms = Math.sqrt(sum / input.length);
        const now = Date.now();
        if (rms > VAD_RMS) lastVoiceMs = now;
        if (silenceMs > 0 && !idleFired && !responseActive && !playing && now - lastVoiceMs > silenceMs) {
          idleFired = true;
          try { onIdle?.(); } catch { /* hook handles teardown */ }
        }
        buf.push(new Float32Array(input));
        total += e.inputBuffer.length;
        while (total >= chunkSamples) {
          const chunk = new Float32Array(chunkSamples);
          let off = 0;
          while (off < chunkSamples && buf.length) {
            const b = buf[0];
            const need = chunkSamples - off;
            if (b.length <= need) { chunk.set(b, off); off += b.length; total -= b.length; buf.shift(); }
            else { chunk.set(b.subarray(0, need), off); buf[0] = b.subarray(need); off += need; total -= need; }
          }
          raw({ realtimeInput: { audio: { data: floatToB64Pcm16(chunk, rate, IN_RATE), mimeType: `audio/pcm;rate=${IN_RATE}` } } });
        }
      };
      source.connect(proc);
      proc.connect(ctx.destination);
    } catch (err) {
      onError(err instanceof Error ? err : new Error(`gemini ws onopen failed: ${String(err)}`));
    }
  };

  ws.onmessage = (e) => {
    let msg: Record<string, unknown>;
    try {
      const text = e.data instanceof ArrayBuffer ? new TextDecoder().decode(e.data) : (e.data as string);
      msg = JSON.parse(text);
    } catch {
      return;
    }
    if (msg.setupComplete !== undefined && !configured) {
      configured = true;
      onEvent({ type: "session.updated" });
      if (session.greeting) raw({ clientContent: { turns: [{ role: "user", parts: [{ text: session.greeting }] }], turnComplete: true } });
      onOpen?.();
      return;
    }
    const sc = msg.serverContent as
      | { modelTurn?: { parts?: { inlineData?: { data?: string; mimeType?: string }; text?: string }[] }; interrupted?: boolean; turnComplete?: boolean; generationComplete?: boolean; inputTranscription?: { text?: string }; outputTranscription?: { text?: string } }
      | undefined;
    if (sc) {
      if (sc.interrupted) {
        stopPlayback();
        agentText = "";
        onEvent({ type: "input_audio_buffer.speech_started" });
      }
      if (sc.inputTranscription?.text) {
        userText += sc.inputTranscription.text;
        onEvent({ type: "conversation.item.input_audio_transcription.updated", item_id: `u${userItem}`, transcript: userText });
      }
      if (sc.outputTranscription?.text) {
        agentText += sc.outputTranscription.text;
        onEvent({ type: "response.output_audio_transcript.delta", delta: sc.outputTranscription.text });
      }
      for (const p of sc.modelTurn?.parts ?? []) {
        const b64 = p.inlineData?.data;
        if (b64) {
          if (!responseActive) { responseActive = true; onEvent({ type: "response.created" }); }
          sawAudioThisTurn = true;
          enqueue(withOutRate(b64Pcm16ToFloat(b64), p.inlineData?.mimeType));
          onEvent({ type: "response.output_audio.delta" });
        } else if (p.text) {
          agentText += p.text;
          onEvent({ type: "response.text.delta", delta: p.text });
        }
      }
      if (sc.turnComplete) {
        if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
        drain(true);
        if (userText) { onEvent({ type: "conversation.item.input_audio_transcription.completed", item_id: `u${userItem}`, transcript: userText }); userText = ""; userItem++; }
        if (agentText || sawAudioThisTurn) onEvent({ type: "response.output_audio_transcript.done", transcript: agentText });
        agentText = "";
        sawAudioThisTurn = false;
        responseActive = false;
        lastVoiceMs = Date.now();
        onEvent({ type: "response.done", response: { output: [] } });
      }
    }
    const tc = msg.toolCall as { functionCalls?: { id?: string; name?: string; args?: unknown }[] } | undefined;
    if (tc?.functionCalls?.length) {
      for (const fc of tc.functionCalls) {
        const id = fc.id || `call_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        if (fc.name) pendingCalls.set(id, fc.name);
        onEvent({ type: "response.function_call_arguments.done", call_id: id, name: fc.name, arguments: JSON.stringify(fc.args ?? {}) });
      }
    }
    // token facts (prompt / response, per modality) — Gemini sends them per turn; the app logs them
    // and the worker relay accumulates the same for the settle
    const um = msg.usageMetadata as { promptTokenCount?: number; responseTokenCount?: number; totalTokenCount?: number } | undefined;
    if (um) onEvent({ type: 'gemini.usage', usage: um });
    const goAway = msg.goAway as { timeLeft?: string } | undefined;
    if (goAway) console.warn("[gemini-live] goAway", goAway.timeLeft);
    if (msg.error) console.error("[gemini-live] error", JSON.stringify(msg.error).slice(0, 500));
  };

  ws.onerror = () => onError(new Error("gemini live websocket error"));
  ws.onclose = (ev) => {
    if (!closed && ev.code !== 1000) onError(new Error(`gemini live ws closed ${ev.code} ${ev.reason || ""}`.trim()));
  };

  return { callId: null, send, close };
}
